/**
 * MOCK · ASSUMED (CT-5722) — the Tasks tab of a record: the actions and
 * human-in-the-loop tasks waiting on it, grouped by when they need attention.
 *
 * The seed is the prototype's (record PER-0104): seven rows to act on now, one
 * this week, one later. The Next Best Action rows are LINKED to the Next Best
 * Action mock: a suggestion that is gone (no suggestions, a started workflow)
 * is gone from here too.
 *
 * QA switch, read from the URL on every request (`?mockTasks=…`):
 *
 *   empty         nothing to do
 *   fail          the list fails
 *   noNba         no Next Best Action rows (same as `?mockNba=noNba`)
 *   action_fail   Take over / Complete / Not now / Dismiss fail
 */

import { getMockEntityHeaders } from './entityHeader.mock.js';
import { listMockNextBestActions } from './nextBestAction.mock.js';

export const TASKS_SCENARIOS = Object.freeze(['empty', 'fail', 'noNba', 'action_fail']);

let scenarioOverride = null;
/** Per record: tasks done (completed, snoozed, dismissed) and handoffs taken over. */
let state = new Map();

const recordKey = (entityType, entityId) => `${entityType}/${entityId}`;
const recordState = (entityType, entityId) => {
  const key = recordKey(entityType, entityId);
  if (!state.has(key)) state.set(key, { gone: new Set(), takenOver: new Set() });
  return state.get(key);
};

const readScenario = () => {
  if (scenarioOverride) return scenarioOverride;
  try {
    const value = new URLSearchParams(window.location.search).get('mockTasks');
    return TASKS_SCENARIOS.includes(value) ? value : null;
  } catch {
    return null;
  }
};

const nameOf = (entityType, entityId) => {
  const rows = getMockEntityHeaders()[entityType] ?? [];
  const row = rows.find((candidate) => (candidate.entity_id ?? candidate.entityId ?? candidate.id) === entityId);
  return row?.title ?? row?.name ?? 'this contact';
};

const ME = { id: 'u-me', name: 'You', is_me: true };
const PRIYA = { id: 'u-priya', name: 'Priya Nair', is_me: false };

const buildTasks = (name) => [
  {
    group: 'act_now',
    id: 'task-timeline',
    nba_id: 'nba-timeline',
    type: 'next_best_action',
    title: 'Send the migration timeline with the data-residency note',
    hint: 'On the card',
    description: `${name} asked for it in writing on Aug 12 and Aug 18 and has no answer yet. The renewal closes in 20 days.`,
    context_line: 'Renewal',
    action: { type: 'review_draft', ref: 'd-timeline' },
  },
  {
    group: 'act_now',
    id: 'task-handoff',
    type: 'handoff',
    title: `Take over ${name}'s deal from Concierge`,
    owner: ME,
    description: 'Concierge qualified the opportunity and needs a person for the next step.',
    due: { kind: 'now', label: 'Due now' },
    action: { type: 'take_over' },
  },
  {
    group: 'act_now',
    id: 'task-checkin',
    type: 'task',
    title: `Book the next check-in with ${name}`,
    owner: PRIYA,
    context_line: 'Account renewal · from a next best action',
    due: { kind: 'overdue', label: 'Overdue since Oct 2' },
    action: { type: 'complete' },
  },
  {
    group: 'act_now',
    id: 'task-hubspot',
    type: 'task',
    title: 'Log the QBR outcomes in HubSpot',
    owner: PRIYA,
    context_line: 'Account renewal · from HubSpot',
    due: { kind: 'overdue', label: 'Overdue since Oct 6' },
    action: { type: 'open_external', ref: 'https://app.hubspot.com/tasks' },
  },
  {
    group: 'act_now',
    id: 'task-reply',
    type: 'reply_needed',
    title: `Reply to ${name}'s email “Evidence pack — auditor follow-up”`,
    owner: ME,
    description:
      '“Evidence pack — auditor follow-up” — Following up for the auditors: they still need the Q3 access review log signed by the control owner.',
    due: { kind: 'date', label: 'Waiting since Today, 09:12' },
    action: { type: 'reply', ref: 'email-evidence-pack' },
  },
  {
    group: 'act_now',
    id: 'task-references',
    nba_id: 'nba-references',
    type: 'next_best_action',
    title: `Answer ${name}'s request for customer references from the call`,
    owner: ME,
    description:
      '“Can you send us references from customers in our industry?” — Sep 2 call · 5:20. Concierge has three ways to answer and recommends one.',
    context_line: 'Buying intent',
    due: { kind: 'date', label: 'Due today' },
    action: { type: 'answer', ref: 'nba-references' },
  },
  {
    group: 'act_now',
    id: 'task-playbook',
    nba_id: 'nba-playbook',
    type: 'next_best_action',
    title: `Add ${name} to the Renewal playbook`,
    description: '5 steps, about 3 weeks.',
    context_line: 'Renewal · needs your confirmation',
    action: { type: 'review_workflow', ref: 'nba-playbook' },
  },
  {
    group: 'this_week',
    id: 'task-daniel',
    nba_id: 'nba-daniel',
    type: 'next_best_action',
    title: 'Bring Daniel Reyes into the renewal',
    description: `Ask ${name} whether to bring Daniel in before signature.`,
    context_line: 'Renewal · needs your confirmation',
    action: { type: 'review_draft', ref: 'd-daniel' },
  },
  {
    group: 'later',
    id: 'task-sandbox',
    type: 'fact_check',
    title: `Review 12 items about ${name} in Sandbox`,
    owner: PRIYA,
    description:
      "Claims the record's agents found, facts due to re-confirm, and statements that disagree. Decide each one in Sandbox.",
    action: { type: 'review_in_sandbox' },
  },
];

const GROUP_ORDER = ['act_now', 'this_week', 'later'];

const matchesSearch = (row, search = {}) => {
  const needle = (search.query ?? '').trim().toLowerCase();
  if (!needle) return true;
  const fields = search.fields?.length ? search.fields : ['title'];
  return fields.some((field) => String(row[field] ?? '').toLowerCase().includes(needle));
};

/** A handoff taken over is the user's task now: one thing left, complete it. */
const takenOver = (row) => ({ ...row, type: 'task', owner: ME, action: { type: 'complete' } });

export const listMockTasks = ({ entityType, entityId, filters, search }) => {
  const scenario = readScenario();
  if (scenario === 'fail') return { error: { status: 500, description: 'Mock tasks failure (?mockTasks=fail).' } };

  const record = recordState(entityType, entityId);
  const nbaList = listMockNextBestActions({ entityType, entityId });
  const liveNbaIds = new Set(scenario === 'noNba' ? [] : (nbaList.body?.items ?? []).map((item) => item.id));
  const types = filters?.type ?? [];

  const rows =
    scenario === 'empty'
      ? []
      : buildTasks(nameOf(entityType, entityId))
          .filter((row) => !row.nba_id || liveNbaIds.has(row.nba_id))
          .filter((row) => !record.gone.has(row.id))
          .map((row) => (record.takenOver.has(row.id) ? takenOver(row) : row))
          .filter((row) => !types.length || types.includes(row.type))
          .filter((row) => matchesSearch(row, search));

  return {
    body: {
      groups: GROUP_ORDER.map((key) => {
        const items = rows
          .filter((row) => row.group === key)
          .map(({ group: _group, nba_id: _nbaId, ...row }) => ({ owner: null, hint: null, context_line: null, due: null, description: null, ...row }));
        return { key, total: items.length, items };
      }),
    },
    meta: { total_items: rows.length },
  };
};

const MUTATIONS = ['take_over', 'complete', 'snooze', 'dismiss'];

export const runMockTaskMutation = ({ entityType, entityId, taskId, mutation }) => {
  if (readScenario() === 'action_fail') {
    return { error: { status: 500, description: 'Mock task failure (?mockTasks=action_fail).' } };
  }
  if (!MUTATIONS.includes(mutation)) return { error: { status: 400, description: `Unknown mutation ${mutation}` } };
  const exists = buildTasks(nameOf(entityType, entityId)).some((row) => row.id === taskId);
  if (!exists) return { error: { status: 404, description: `No task ${taskId}` } };

  const record = recordState(entityType, entityId);
  if (mutation === 'take_over') record.takenOver.add(taskId);
  else record.gone.add(taskId);
  return { body: { task_id: taskId, mutation }, meta: {} };
};

export const __setMockTasksScenario = (value) => {
  scenarioOverride = value;
};

export const __resetMockTasksForTests = () => {
  scenarioOverride = null;
  state = new Map();
};
